import React, { useLayoutEffect, useState } from 'react';
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import './App.css';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import NavBar from '@/components/layout/NavBar/NavBar';
import Footer from '@/components/layout/Footer/Footer';
import {
    ProtectedRoutes,
    PublicRoutes,
    AuthRestrictedRoutes,
    ComingSoon,
    JoinEcosystemBanner,
    TermlyCMP,
} from '@/components/common';
import EcosystemAdminRoute from '@/shared/routes/EcosystemAdminRoute';
import CapabilityRoute from '@/shared/routes/CapabilityRoute';
import TenantRoute from '@/shared/routes/TenantRoute';
import SignUp from '@/features/auth/components/SignUp';
import ForgotPassword from '@/features/auth/components/ForgotPassword';
import LogIn from '@/features/auth/components/LogIn';
import EcosystemChooser from '@/features/auth/components/EcosystemChooser';
import ProfilePage from '@/features/profile/components/ProfilePage';
import AccountPage from '@/features/profile/components/AccountPage';
import CreateProfile from '@/features/profile/components/CreateProfile';
import EditProfile from '@/features/profile/components/EditProfile';
import About from '@/features/platform/About';
import { EcosystemContentPage, ContentEditorPage } from '@/features/content';
import ContactUs from '@/features/contact/ContactUs';
import InviteBannerContainer from '@/features/invite/components/InviteBannerContainer';
import FeedbackPage from '@/features/feedback/FeedbackPage';
import MainPolicies from '@/features/policies/MainPolicies';
import Dashboard from '@/features/dashboard/Dashboard';
import OrganizationDetail from '@/features/organizations/OrganizationDetail';
import OrganizationOnboarding from '@/features/organizations/OrganizationOnboarding';
import OrganizationEdit from '@/features/organizations/OrganizationEdit';
import Directory from '@/features/directory/Directory';
import EcosystemPage from '@/features/ecosystem/EcosystemPage';
import {
    ConnectionsPage,
    ConnectionsIndexRedirect,
    MentorDirectory,
    FindPartners,
} from '@/features/connections';
import Assistant from '@/features/assistant/Assistant';
import {
    AdminDashboard,
    AdminOrganizations,
    AdminClaims,
} from '@/features/admin';
import ChatWidget from '@/features/assistant/components/ChatWidget';
import {
    OpportunitiesPage,
    OpportunityDetail,
    OpportunityForm,
} from '@/features/opportunities';
import { Landing, Mission, EcosystemsPage } from '@/features/platform';
// Aliased: distinct from the generic tenant-fallback `ComingSoon` above
// (@/components/common) — this is the ticket #205 pre-launch placeholder,
// now routed at its own `/coming-soon` path (#353) rather than standing in
// for `Landing` at `/`. See the `/coming-soon` Route below.
import PreLaunchComingSoon from '@/features/platform/ComingSoon';
import {
    PlatformAdminPage,
    PlatformAdminRoute,
} from '@/features/platform-admin';

function App() {
    const location = useLocation();
    const navigationType = useNavigationType();
    const isAssistantPage = location.pathname.startsWith('/assistant');
    // #353: the pre-launch waitlist page is the only page production traffic
    // can reach before launch, so it must carry no navigation chrome at all —
    // no NavBar, no Footer, no invite/join banners — to satisfy "no
    // navigation elements pointing to other parts of the app".
    // Exact match, with an optional trailing slash. `startsWith` also matched
    // `/coming-soonish` and `/coming-soon/anything`, which fall through to the
    // `path="*"` catch-all and render NotFound — with the NavBar and Footer
    // suppressed. Stripping every escape route from the page whose whole job is
    // helping a lost user is the worst possible place to do it.
    const isComingSoonPage =
        location.pathname === '/coming-soon' ||
        location.pathname === '/coming-soon/';
    const { isPlatform } = useEcosystem();
    // The invite banner and the default join prompt are mutually exclusive.
    // InviteBannerContainer persists via sessionStorage (not the URL), so it
    // reports whether it's showing and the join prompt stands down from that
    // same source of truth — no double banner, even after Explore drops
    // `?invite=` from the URL (#171).
    const [inviteBannerActive, setInviteBannerActive] = useState(false);

    useLayoutEffect(() => {
        if (navigationType !== 'POP') {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
    }, [location.pathname, navigationType]);

    // Fallbacks for the tenant home / about / mission routes when no authored
    // EcosystemContent is published (#262: the apex no longer uses the CMS at
    // all, so it has no fallback to compute here — it always renders its
    // hardcoded page directly, see the Route definitions below).
    const homeFallback = (
        <ComingSoon
            title="Home"
            message="This ecosystem has not published a landing page yet."
            icon="🏠"
            backLabel="View Ecosystem"
            backPath="/directory"
        />
    );
    const aboutFallback = (
        <ComingSoon
            title="About"
            message="This ecosystem has not published an about page yet."
            icon="ℹ️"
            backLabel="Back to Home"
            backPath="/"
        />
    );
    const missionFallback = (
        <ComingSoon
            title="Mission"
            message="This ecosystem has not published a mission page yet."
            icon="🎯"
            backLabel="Back to Home"
            backPath="/"
        />
    );
    // Contact has no apex fallback: the apex `/contact` route below renders
    // the hardcoded ContactUs directly and never mounts EcosystemContentPage,
    // since the apex has no tenant admin to author CMS contact content.
    const contactFallback = (
        <ComingSoon
            title="Contact"
            message="This ecosystem has not published a contact page yet."
            icon="✉️"
            backLabel="Back to Home"
            backPath="/"
        />
    );

    return (
        <div className="App">
            <TermlyCMP websiteUUID="f16f3b66-bf3f-4fdf-8bea-525045652ca9" />
            {!isComingSoonPage && (
                <header id="App-header">
                    <NavBar />
                </header>
            )}
            <main id="App-main">
                {/* Invite banner: renders only when a valid `?invite=` token is
                    present (Phase 2, #171). Reports its visibility so the join
                    prompt below can stand down from the same signal. Suppressed
                    on the Coming Soon page (#353) along with the rest of the
                    nav chrome — see `isComingSoonPage` above. */}
                {!isComingSoonPage && (
                    <InviteBannerContainer
                        onVisibilityChange={setInviteBannerActive}
                    />
                )}
                {/* Tenant join prompt: self-hiding in platform mode, for
                    signed-out visitors, and for existing members. Also
                    suppressed whenever the invite banner is showing, so the two
                    never appear at once. */}
                {!isComingSoonPage && !inviteBannerActive && (
                    <JoinEcosystemBanner />
                )}
                <Routes>
                    {/* Public routes: accessible to everyone */}
                    {/* Landing / about / mission pages: the apex (#262) is
                        purely hardcoded and never touches the CMS. Tenants
                        render authored EcosystemContent when published, else
                        fall back to a ComingSoon placeholder.

                        The apex `/` renders `Landing` again as of #353: the
                        pre-launch placeholder previously stood in here
                        (ticket #205) but has moved to its own `/coming-soon`
                        route below so the team can keep developing and
                        testing the real Landing page in dev while
                        `/coming-soon` is the only page pushed live to
                        production ahead of launch. */}
                    <Route
                        path="/"
                        element={
                            <PublicRoutes>
                                {isPlatform ? (
                                    <Landing />
                                ) : (
                                    <EcosystemContentPage
                                        pageKey="landing"
                                        fallback={homeFallback}
                                    />
                                )}
                            </PublicRoutes>
                        }
                    />
                    {/* Pre-launch waitlist page (#205, moved off `/` by
                        #353). Renders unconditionally — no `isPlatform`
                        branch — since it must be reachable and identical
                        regardless of tenant resolution, and is the only page
                        production traffic can reach until the platform
                        launches. Has no nav chrome (see the
                        `isComingSoonPage` NavBar/Footer suppression below).
                        `coming-soon` is a reserved slug
                        (server/src/config/reserved-slugs.js) so no tenant
                        can ever shadow this route.

                        Deliberately NOT wrapped in `PublicRoutes`: that guard
                        redirects a signed-in, verified user with no profile to
                        `/create-profile`, which is inside the application this
                        page exists to conceal, and it renders a bare
                        `<div>Loading...</div>` while auth settles, so the
                        intended front door flashed unstyled text on every load.
                        The page is public, static, and has nothing to guard. */}
                    <Route
                        path="/coming-soon"
                        element={<PreLaunchComingSoon />}
                    />
                    <Route
                        path="/about"
                        element={
                            <PublicRoutes>
                                {isPlatform ? (
                                    <About />
                                ) : (
                                    <EcosystemContentPage
                                        pageKey="about"
                                        fallback={aboutFallback}
                                    />
                                )}
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/mission"
                        element={
                            <PublicRoutes>
                                {isPlatform ? (
                                    <Mission />
                                ) : (
                                    <EcosystemContentPage
                                        pageKey="mission"
                                        fallback={missionFallback}
                                    />
                                )}
                            </PublicRoutes>
                        }
                    />
                    {/* Contact: tenant-only CMS content. The apex has no
                        tenant admin to author it, so the apex keeps rendering
                        the hardcoded ContactUs directly rather than mounting
                        EcosystemContentPage — unlike landing/about/mission,
                        no CMS-authored content can ever reach the apex page. */}
                    <Route
                        path="/contact"
                        element={
                            <PublicRoutes>
                                {isPlatform ? (
                                    <ContactUs />
                                ) : (
                                    <EcosystemContentPage
                                        pageKey="contact"
                                        fallback={contactFallback}
                                    />
                                )}
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/feedback"
                        element={
                            <PublicRoutes>
                                <FeedbackPage />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/policies/*"
                        element={
                            <PublicRoutes>
                                <MainPolicies />
                            </PublicRoutes>
                        }
                    />
                    {/* /organizations route removed - consolidated into /directory */}
                    <Route
                        path="/organizations/onboard"
                        element={
                            <ProtectedRoutes>
                                <OrganizationOnboarding />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/organizations/:id"
                        element={
                            <PublicRoutes>
                                <OrganizationDetail />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/organizations/:id/edit"
                        element={
                            <ProtectedRoutes>
                                <OrganizationEdit />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/organizations/:orgId/opportunities/new"
                        element={
                            <CapabilityRoute name="opportunities">
                                <ProtectedRoutes>
                                    <OpportunityForm mode="create" />
                                </ProtectedRoutes>
                            </CapabilityRoute>
                        }
                    />
                    <Route
                        path="/organizations/:orgId/opportunities/:id/edit"
                        element={
                            <CapabilityRoute name="opportunities">
                                <ProtectedRoutes>
                                    <OpportunityForm mode="edit" />
                                </ProtectedRoutes>
                            </CapabilityRoute>
                        }
                    />
                    <Route
                        path="/directory"
                        element={
                            <TenantRoute>
                                <PublicRoutes>
                                    <Directory />
                                </PublicRoutes>
                            </TenantRoute>
                        }
                    />
                    <Route
                        path="/ecosystem"
                        element={
                            <TenantRoute>
                                <CapabilityRoute name="ecosystemAnalytics">
                                    <PublicRoutes>
                                        <EcosystemPage />
                                    </PublicRoutes>
                                </CapabilityRoute>
                            </TenantRoute>
                        }
                    />
                    <Route
                        path="/connections"
                        element={
                            <TenantRoute>
                                <ConnectionsPage />
                            </TenantRoute>
                        }
                    >
                        <Route index element={<ConnectionsIndexRedirect />} />
                        <Route
                            path="mentors"
                            element={
                                <CapabilityRoute name="mentoring">
                                    <PublicRoutes>
                                        <MentorDirectory />
                                    </PublicRoutes>
                                </CapabilityRoute>
                            }
                        />
                        <Route
                            path="partners"
                            element={
                                <CapabilityRoute name="discoverPartners">
                                    <PublicRoutes>
                                        <FindPartners />
                                    </PublicRoutes>
                                </CapabilityRoute>
                            }
                        />
                    </Route>
                    <Route
                        path="/mentors"
                        element={<Navigate to="/connections/mentors" replace />}
                    />
                    <Route
                        path="/partners"
                        element={
                            <Navigate to="/connections/partners" replace />
                        }
                    />
                    <Route
                        path="/opportunities"
                        element={
                            <TenantRoute>
                                <CapabilityRoute name="opportunities">
                                    <PublicRoutes>
                                        <OpportunitiesPage />
                                    </PublicRoutes>
                                </CapabilityRoute>
                            </TenantRoute>
                        }
                    />
                    <Route
                        path="/opportunities/:id"
                        element={
                            <CapabilityRoute name="opportunities">
                                <PublicRoutes>
                                    <OpportunityDetail />
                                </PublicRoutes>
                            </CapabilityRoute>
                        }
                    />

                    {/* Platform apex surface (P7 wave 2, merged in #241).
                        One route for browsing hosted ecosystems: card view
                        and ecosystem-of-ecosystems graph view, toggled via
                        `?view=cards|graph`. `ecosystems` is a reserved slug,
                        so this is safe as a platform route: a tenant can
                        never shadow it. It consumes the platform-level
                        `GET /api/ecosystems` endpoint and is not tenant-
                        scoped; it renders the same public data regardless
                        of whether the URL resolved a tenant. The old
                        `/ecosystems/graph` route redirects so bookmarks and
                        stale external links keep working. */}
                    <Route
                        path="/ecosystems"
                        element={
                            <PublicRoutes>
                                <EcosystemsPage />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/ecosystems/graph"
                        element={
                            <Navigate to="/ecosystems?view=graph" replace />
                        }
                    />

                    {/* Auth-Restricted Routes (only for unauthenticated users) */}
                    <Route
                        path="/login"
                        element={
                            <AuthRestrictedRoutes>
                                <LogIn />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            <AuthRestrictedRoutes>
                                <SignUp />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <AuthRestrictedRoutes>
                                <ForgotPassword />
                            </AuthRestrictedRoutes>
                        }
                    />

                    {/* Protected Routes (only for authenticated users) */}
                    <Route
                        path="/create-profile"
                        element={
                            <ProtectedRoutes>
                                <CreateProfile />
                            </ProtectedRoutes>
                        }
                    />
                    {/* Post-auth ecosystem hand-off (Wave K). Every post-login
                        entry point (LogIn, CreateProfile, OrganizationOnboarding,
                        the ProtectedRoutes apex-tenant guard) routes here, and
                        the chooser branches on 0 / 1 / N memberships: 0 →
                        welcome + /ecosystems CTA; 1 → full-reload to that
                        tenant's /dashboard; N → picker. The route is platform-
                        level (it renders against any tenant context, including
                        none) so it is reachable from apex and from any tenant. */}
                    <Route
                        path="/choose-ecosystem"
                        element={
                            <ProtectedRoutes>
                                <EcosystemChooser />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoutes>
                                <ProfilePage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/profile/edit"
                        element={
                            <ProtectedRoutes>
                                <EditProfile />
                            </ProtectedRoutes>
                        }
                    />
                    {/* Platform-level account page: identity plus ecosystem
                        memberships. Renders only at the apex; AccountPage
                        redirects a visit that arrives with a tenant resolved.
                        The `/profile` route above redirects here at the
                        apex. */}
                    <Route
                        path="/account"
                        element={
                            <ProtectedRoutes>
                                <AccountPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoutes>
                                <Dashboard />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/assistant"
                        element={
                            <CapabilityRoute name="aiAssistant">
                                <ProtectedRoutes>
                                    <Assistant />
                                </ProtectedRoutes>
                            </CapabilityRoute>
                        }
                    />
                    <Route
                        path="/assistant/:persona"
                        element={
                            <CapabilityRoute name="aiAssistant">
                                <ProtectedRoutes>
                                    <Assistant />
                                </ProtectedRoutes>
                            </CapabilityRoute>
                        }
                    />

                    {/* Per-tenant admin area (org approvals, ownership
                        claims, admin dashboard). Gated by EcosystemAdminRoute:
                        strict per-tenant guard requiring an `admin`
                        EcosystemMembership in the active tenant. Mirrors the
                        server's `isAdminOfCurrentEcosystem`. No platform-
                        admin override: these surfaces are tenant-scoped, and
                        platform super-admins use /platform-admin instead. */}
                    <Route
                        path="/admin"
                        element={
                            <EcosystemAdminRoute>
                                <AdminDashboard />
                            </EcosystemAdminRoute>
                        }
                    />
                    <Route
                        path="/admin/organizations"
                        element={
                            <EcosystemAdminRoute>
                                <AdminOrganizations />
                            </EcosystemAdminRoute>
                        }
                    />
                    <Route
                        path="/admin/claims"
                        element={
                            <EcosystemAdminRoute>
                                <AdminClaims />
                            </EcosystemAdminRoute>
                        }
                    />
                    {/* Admin content editor (P5 wave 3a). Tenant-only since
                        #262 stripped the CMS off the apex: gated by the
                        strict EcosystemAdminRoute (`admin` EcosystemMembership
                        in the current tenant), mirroring the server's
                        `isContentAdmin`. The route itself is omitted on the
                        apex so it falls through to the 404 catch-all below.
                        :pageKey is landing | about | mission. */}
                    {!isPlatform && (
                        <Route
                            path="/admin/content/:pageKey"
                            element={
                                <EcosystemAdminRoute>
                                    <ContentEditorPage />
                                </EcosystemAdminRoute>
                            }
                        />
                    )}

                    {/* Platform administration (P7 wave 3a). Gated by
                        PlatformAdminRoute: the caller's platform-wide
                        `User.role` must be `platformAdmin`. Distinct from the
                        per-ecosystem `/admin/*` area above, which gates on a
                        per-tenant EcosystemMembership role. `platform-admin`
                        is not a reserved slug and resolves in platform mode,
                        so it is safe as a platform route. */}
                    <Route
                        path="/platform-admin"
                        element={
                            <PlatformAdminRoute>
                                <PlatformAdminPage />
                            </PlatformAdminRoute>
                        }
                    />

                    {/* 404 catch-all */}
                    <Route
                        path="*"
                        element={
                            <ComingSoon
                                title="Page Not Found"
                                message="The page you are looking for does not exist."
                                icon="🔍"
                                backLabel="Back to Home"
                                backPath="/"
                            />
                        }
                    />
                </Routes>
            </main>
            {!isAssistantPage && !isComingSoonPage && (
                <footer id="App-footer">
                    <Footer />
                </footer>
            )}
            {/* ChatWidget renders internal <Link>s (e.g. to /login) once
                opened, so it's suppressed here too, not just NavBar/Footer. */}
            {!isComingSoonPage && <ChatWidget />}
        </div>
    );
}

export default App;
